
import { useState } from "react";
import {
  Users, FileText, Calendar,
  Newspaper, Globe, HelpCircle, Briefcase,
  X, ExternalLink, Home, Phone,
  GraduationCap, ChevronDown, Navigation, Smartphone,
  ClipboardList, Building2, ImageIcon,
  Search,
} from "lucide-react";
import { useSiteLogo } from "@/hooks/useSiteLogo";

// Social icons (deprecated in lucide but still functional)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Instagram = (props: any) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" {...props}>
    <rect width="20" height="20" x="2" y="2" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" x2="17.51" y1="6.5" y2="6.5"/>
  </svg>
);
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Youtube = (props: any) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M22.54 6.42a2.78 2.78 0 0 0-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46A2.78 2.78 0 0 0 1.46 6.42 29 29 0 0 0 1 12a29 29 0 0 0 .46 5.58A2.78 2.78 0 0 0 3.41 19.6C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 0 0 1.95-1.95A29 29 0 0 0 23 12a29 29 0 0 0-.46-5.58z"/><polygon points="9.75 15.02 15.5 12 9.75 8.98 9.75 15.02"/>
  </svg>
);
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Facebook = (props: any) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
  </svg>
);

interface AdminSidebarProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
  open: boolean;
  onClose: () => void;
}

const sections = [
  {
    label: "Site",
    defaultOpen: true,
    items: [
      { id: "inicio",    title: "Início",             icon: Home },
      { id: "historia",  title: "Quem Somos",         icon: FileText },
      { id: "servicos",    title: "Serviços",           icon: Briefcase },
      { id: "avaliacoes",  title: "Avaliações",         icon: ClipboardList },
      { id: "consultorios",title: "Consultórios",       icon: Building2 },
      { id: "contato",     title: "Contato",            icon: Phone },
      { id: "blog",      title: "Blog",               icon: FileText },
      { id: "imprensa",  title: "Imprensa",           icon: Newspaper },
      { id: "mobile",    title: "Página Mobile",      icon: Smartphone },
      { id: "navegacao", title: "Cabeçalho & Rodapé", icon: Navigation },
      { id: "logo",      title: "Logo do Site",        icon: ImageIcon },
      { id: "seo",       title: "SEO",                 icon: Search },
    ],
  },
  {
    label: "Gestão",
    defaultOpen: true,
    items: [
      { id: "usuarios",   title: "Usuários & Permissões", icon: Users },
      { id: "calendario", title: "Calendário",            icon: Calendar },
    ],
  },
  {
    label: "Redes Sociais",
    defaultOpen: false,
    items: [
      { id: "instagram", title: "Instagram", icon: Instagram, external: "https://business.instagram.com/" },
      { id: "youtube",   title: "YouTube",   icon: Youtube,   external: "https://studio.youtube.com/" },
      { id: "facebook",  title: "Facebook",  icon: Facebook,  external: "https://business.facebook.com/" },
    ],
  },
  {
    label: "Ajuda",
    defaultOpen: false,
    items: [
      { id: "tutorial", title: "Tutorial do Painel", icon: GraduationCap },
    ],
  },
];

// ── Collapsible section ────────────────────────────────────────────────────────
function SidebarSection({
  section, activeTab, onItemClick,
}: {
  section: typeof sections[0];
  activeTab: string;
  onItemClick: (id: string, external?: string) => void;
}) {
  const [open, setOpen] = useState(section.defaultOpen);

  return (
    <div>
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-3 mb-1 group"
      >
        <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-white/30 group-hover:text-white/50 transition-colors">
          {section.label}
        </p>
        <ChevronDown
          className={`w-3 h-3 text-white/20 group-hover:text-white/40 transition-all duration-200 ${open ? "rotate-0" : "-rotate-90"}`}
        />
      </button>

      <div className={`space-y-0.5 overflow-hidden transition-all duration-200 ${open ? "max-h-[600px] opacity-100" : "max-h-0 opacity-0"}`}>
        {section.items.map((item) => {
          const isActive   = activeTab === item.id;
          const isExternal = !!(item as any).external;

          return (
            <button
              key={item.id}
              onClick={() => onItemClick(item.id, (item as any).external)}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-all duration-150 group ${
                isActive
                  ? "bg-white text-brand-primary font-semibold shadow-sm"
                  : "text-white/60 hover:text-white hover:bg-white/10"
              }`}
            >
              <div className="flex items-center gap-2.5">
                <item.icon className={`w-3.5 h-3.5 shrink-0 ${isActive ? "text-brand-primary" : ""}`} />
                <span>{item.title}</span>
              </div>
              {isExternal && (
                <ExternalLink className="w-3 h-3 opacity-40 group-hover:opacity-70" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Main sidebar ───────────────────────────────────────────────────────────────
export function AdminSidebar({ activeTab, onTabChange, open, onClose }: AdminSidebarProps) {
  const isDesktop = () => window.innerWidth >= 1024;
  const logoUrl = useSiteLogo();

  const handleItemClick = (id: string, external?: string) => {
    if (external) {
      window.open(external, "_blank");
    } else {
      onTabChange(id);
      if (!isDesktop()) onClose();
    }
  };

  return (
    <>
      {/* Overlay — mobile only */}
      {open && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden backdrop-blur-sm" onClick={onClose} />
      )}

      {/* Drawer */}
      <aside
        className={`fixed top-0 left-0 h-full w-64 z-50 flex flex-col transform transition-transform duration-300 ease-in-out ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
        style={{ background: "linear-gradient(160deg, #1a4d30 0%, #1F5938 60%, #17472e 100%)" }}
      >
        {/* Logo / brand */}
        <div className="px-5 pt-6 pb-5 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            {logoUrl ? (
              <img src={logoUrl} alt="Logo" className="w-10 h-10 rounded-xl object-cover shadow-sm select-none shrink-0" />
            ) : (
              <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shadow-sm select-none shrink-0">
                <span className="text-xl leading-none">⚜️</span>
              </div>
            )}
            <div>
              <p className="text-white font-bold text-sm font-primary leading-tight">Clínica Equilíbrio</p>
              <p className="text-white/40 text-[10px] uppercase tracking-widest mt-0.5">Painel Admin</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 rounded-lg flex items-center justify-center text-white/40 hover:text-white hover:bg-white/10 transition-colors"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Divider */}
        <div className="mx-5 h-px bg-white/10 shrink-0" />

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-4">
          {sections.map((section) => (
            <SidebarSection
              key={section.label}
              section={section}
              activeTab={activeTab}
              onItemClick={handleItemClick}
            />
          ))}
        </nav>

        {/* Bottom: link to site */}
        <div className="px-3 pb-5 shrink-0">
          <div className="h-px bg-white/10 mb-4" />
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-white/40 hover:text-white hover:bg-white/10 transition-colors text-sm"
          >
            <Globe className="w-3.5 h-3.5" />
            <span>Ver site público</span>
            <ExternalLink className="w-3 h-3 ml-auto opacity-50" />
          </a>
        </div>
      </aside>
    </>
  );
}
